import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';
import NotificationBell from '../components/NotificationBell';
import UnifiedCheckout from '../components/UnifiedCheckout';
import {
  Video,
  Calendar,
  Clock,
  Users,
  BookOpen,
  Loader2,
  CheckCircle,
  Play,
  ExternalLink,
  Search,
  Filter,
  Star,
  Award,
  Tv,
  Youtube,
  MessageSquare,
  Bell,
  BellRing,
  ArrowRight,
  Timer,
  Mic,
  Sparkles,
  Eye,
  CreditCard,
  Lock,
  ShoppingCart
} from 'lucide-react';

const WebinarsPage = () => {
  const [webinars, setWebinars] = useState([]);
  const [pastWebinars, setPastWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [registering, setRegistering] = useState(null);
  const [checkoutModal, setCheckoutModal] = useState({ open: false, webinar: null });
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMyRegistrations();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [upcomingRes, pastRes, categoriesRes] = await Promise.all([
        api.get('/webinars?upcoming_only=true'),
        api.get('/webinars?upcoming_only=false'),
        api.get('/webinars/categories')
      ]);
      setWebinars(upcomingRes.data);
      // Filter past webinars (those with recordings)
      const past = pastRes.data.filter(w => w.has_recording || new Date(w.date) < new Date());
      setPastWebinars(past.slice(0, 6));
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching webinars:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRegistrations = async () => {
    try {
      const response = await api.get('/dashboard/webinar-registrations');
      setMyRegistrations(response.data.map(r => r.webinar_id));
    } catch (error) {
      // User might not have registrations
      setMyRegistrations([]);
    }
  };

  const handleRegister = async (webinarId, webinar) => {
    if (!isAuthenticated) {
      toast({
        title: "Inicia sesión",
        description: "Debes iniciar sesión para registrarte al webinar",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }

    const isPaid = webinar.price && webinar.price > 0;

    if (isPaid) {
      // Open checkout modal for paid webinars
      setCheckoutModal({ open: true, webinar: webinar });
      return;
    }

    // For free webinars, register directly
    setRegistering(webinarId);
    try {
      const response = await api.post(`/webinars/${webinarId}/register`);
      setMyRegistrations(prev => [...prev, webinarId]);
      toast({
        title: "¡Registro exitoso!",
        description: "Te hemos registrado al webinar. Recibirás un recordatorio por email.",
      });
      fetchData();
    } catch (error) {
      // Check if already registered
      if (error.response?.data?.detail === "Ya estás registrado en este webinar") {
        setMyRegistrations(prev => [...prev, webinarId]);
        toast({
          title: "Ya registrado",
          description: "Ya estás inscrito en este webinar",
        });
      } else if (error.response?.data?.detail === "Ya has pagado este webinar") {
        setMyRegistrations(prev => [...prev, webinarId]);
        toast({
          title: "Ya pagado",
          description: "Ya tienes acceso a este webinar",
        });
      } else {
        toast({
          title: "Error",
          description: error.response?.data?.detail || "No se pudo completar el registro",
          variant: "destructive"
        });
      }
    } finally {
      setRegistering(null);
    }
  };

  const handleCheckoutComplete = (result) => {
    if (result) {
      fetchData();
      fetchMyRegistrations();
      toast({
        title: "¡Éxito!",
        description: result.free ? "Acceso gratuito otorgado" : "Pago procesado correctamente"
      });
    }
    setCheckoutModal({ open: false, webinar: null });
  };

  const filteredWebinars = webinars.filter(w => {
    const matchesCategory = selectedCategory === 'all' || w.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      w.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.instructor?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha por confirmar';
    return new Date(dateString).toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeUntil = (dateString) => {
    if (!dateString) return null;
    const now = new Date();
    const webinarDate = new Date(dateString);
    const diff = webinarDate - now;
    
    if (diff < 0) return 'En vivo ahora';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `En ${days} día${days > 1 ? 's' : ''}`;
    if (hours > 0) return `En ${hours} hora${hours > 1 ? 's' : ''}`;
    return 'Próximamente';
  };

  const levelConfig = {
    basico: { color: 'bg-green-100 text-green-700', label: 'Básico' },
    intermedio: { color: 'bg-yellow-100 text-yellow-700', label: 'Intermedio' },
    avanzado: { color: 'bg-red-100 text-red-700', label: 'Avanzado' }
  };

  const platformConfig = {
    youtube: { icon: Youtube, label: 'YouTube Live', color: 'text-red-600' },
    zoom: { icon: Video, label: 'Zoom', color: 'text-blue-600' },
    meet: { icon: Video, label: 'Google Meet', color: 'text-green-600' },
    vimeo: { icon: Tv, label: 'Vimeo', color: 'text-cyan-600' }
  };

  const isRegistered = (webinarId) => myRegistrations.includes(webinarId);

  return (
    <Layout>
      {/* Hero Section - WebinarJam Style */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.05),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(255,255,255,0.03),transparent_40%)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-white/10 text-white border border-white/20 px-4 py-1.5">
                <Sparkles className="h-4 w-4 mr-2" />
                Capacitación en Vivo
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="webinars-title">
                Webinars <span className="text-gray-400">UPIM</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Capacitaciones en vivo con los mejores expertos del sector inmobiliario. 
                <strong className="text-white"> Sin límite de asistentes.</strong> Accede desde cualquier dispositivo.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center space-x-2 text-gray-400">
                  <Users className="h-5 w-5" />
                  <span>Usuarios ilimitados</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <Play className="h-5 w-5" />
                  <span>Grabaciones disponibles</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <MessageSquare className="h-5 w-5" />
                  <span>Chat en vivo</span>
                </div>
              </div>

              {!isAuthenticated && (
                <Button 
                  size="lg" 
                  className="bg-white text-gray-900 hover:bg-gray-100 h-14 px-8"
                  onClick={() => navigate('/login')}
                  data-testid="login-to-register"
                >
                  Inicia sesión para registrarte
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              )}
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <Tv className="h-8 w-8 text-white mb-3" />
                <p className="text-3xl font-bold text-white">52+</p>
                <p className="text-gray-400 text-sm">Webinars al año</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <Users className="h-8 w-8 text-white mb-3" />
                <p className="text-3xl font-bold text-white">∞</p>
                <p className="text-gray-400 text-sm">Asistentes ilimitados</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <Play className="h-8 w-8 text-white mb-3" />
                <p className="text-3xl font-bold text-white">100+</p>
                <p className="text-gray-400 text-sm">Grabaciones</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <Award className="h-8 w-8 text-white mb-3" />
                <p className="text-3xl font-bold text-white">$0</p>
                <p className="text-gray-400 text-sm">Gratis para afiliados</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-6 bg-white border-b sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar webinars..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-webinars"
              />
            </div>
            
            {/* Category Filters */}
            <div className="flex items-center space-x-2 overflow-x-auto pb-2 md:pb-0">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('all')}
                className={selectedCategory === 'all' ? 'bg-gray-900' : ''}
                data-testid="filter-all"
              >
                Todos
              </Button>
              {categories.map(cat => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`whitespace-nowrap ${selectedCategory === cat.id ? 'bg-gray-900' : ''}`}
                >
                  {cat.name}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Webinars */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Próximos Webinars</h2>
              <p className="text-gray-600">Regístrate y recibe recordatorio por email</p>
            </div>
            <Badge className="bg-green-100 text-green-700">
              <Bell className="h-3.5 w-3.5 mr-1" />
              {webinars.length} programados
            </Badge>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredWebinars.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
              <Video className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">No hay webinars programados</p>
              <p className="text-sm text-gray-400">Vuelve pronto o revisa las grabaciones</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredWebinars.map(webinar => {
                const platform = platformConfig[webinar.platform] || platformConfig.youtube;
                const level = levelConfig[webinar.level] || levelConfig.basico;
                const timeUntil = getTimeUntil(webinar.date);
                const registered = isRegistered(webinar.id);
                
                return (
                  <Card 
                    key={webinar.id} 
                    className="overflow-hidden hover:shadow-xl transition-all duration-300 group border-0 shadow-md"
                    data-testid={`webinar-card-${webinar.id}`}
                  >
                    {/* Header with gradient */}
                    <div className="bg-gradient-to-br from-gray-800 via-gray-900 to-black p-5 relative">
                      <div className="flex items-center justify-between mb-4">
                        <Badge className={level.color}>
                          {level.label}
                        </Badge>
                        {timeUntil && (
                          <Badge className="bg-white/20 text-white border-0">
                            <Timer className="h-3 w-3 mr-1" />
                            {timeUntil}
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-white font-bold text-lg leading-tight mb-2 group-hover:text-gray-200 transition-colors">
                        {webinar.title}
                      </h3>
                      <div className="flex items-center text-gray-400 text-sm">
                        <Mic className="h-4 w-4 mr-1.5" />
                        {webinar.instructor || 'Instructor UPIM'}
                      </div>
                      
                      {/* Live indicator for upcoming */}
                      {timeUntil === 'En vivo ahora' && (
                        <div className="absolute top-4 right-4">
                          <span className="flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <CardContent className="p-5">
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2 min-h-[40px]">
                        {webinar.description || 'Capacitación en vivo para profesionales inmobiliarios'}
                      </p>
                      
                      <div className="space-y-2.5 text-sm mb-5">
                        <div className="flex items-center text-gray-600">
                          <Calendar className="h-4 w-4 mr-2.5 text-gray-400" />
                          <span className="capitalize">{formatDate(webinar.date)}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Clock className="h-4 w-4 mr-2.5 text-gray-400" />
                          {formatTime(webinar.date)} • {webinar.duration_minutes} min
                        </div>
                        <div className="flex items-center text-gray-600">
                          <platform.icon className={`h-4 w-4 mr-2.5 ${platform.color}`} />
                          {platform.label}
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Eye className="h-4 w-4 mr-2.5 text-gray-400" />
                          {webinar.current_attendees || 0} registrados • Sin límite
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div>
                          {webinar.is_free ? (
                            <Badge className="bg-green-100 text-green-700 font-medium">
                              Gratis
                            </Badge>
                          ) : (
                            <div className="flex flex-col">
                              <span className="font-bold text-gray-900 text-lg">
                                ${webinar.price?.toLocaleString()} {webinar.currency || 'MXN'}
                              </span>
                              <span className="text-xs text-gray-500">Acceso único</span>
                            </div>
                          )}
                        </div>
                        
                        {registered ? (
                          <div className="flex items-center space-x-2">
                            <NotificationBell 
                              webinar={webinar} 
                              webinarUrl={`${window.location.origin}/webinars/${webinar.id}/sala`}
                              variant="outline"
                            />
                            <Button 
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => navigate(`/webinars/${webinar.id}/sala`)}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Sala
                            </Button>
                          </div>
                        ) : webinar.is_free ? (
                          <Button
                            className="bg-gray-900 hover:bg-gray-800"
                            onClick={() => handleRegister(webinar.id, webinar)}
                            disabled={registering === webinar.id}
                            data-testid={`register-${webinar.id}`}
                          >
                            {registering === webinar.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Bell className="h-4 w-4 mr-2" />
                                Inscribirme
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md"
                            onClick={() => handleRegister(webinar.id, webinar)}
                            disabled={registering === webinar.id}
                            data-testid={`buy-${webinar.id}`}
                          >
                            {registering === webinar.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <ShoppingCart className="h-4 w-4 mr-2" />
                                Comprar
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Past Webinars / Recordings */}
      {pastWebinars.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Grabaciones Disponibles</h2>
                <p className="text-gray-600">Accede a webinars anteriores con tu membresía</p>
              </div>
              <Badge className="bg-purple-100 text-purple-700">
                <Play className="h-3.5 w-3.5 mr-1" />
                {pastWebinars.length}+ grabaciones
              </Badge>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pastWebinars.map(webinar => (
                <Card 
                  key={webinar.id} 
                  className="overflow-hidden hover:shadow-lg transition-all group cursor-pointer"
                >
                  <div className="relative bg-gradient-to-br from-purple-900 to-purple-700 p-5">
                    <div className="absolute inset-0 bg-black/20" />
                    <div className="relative">
                      <Badge className="bg-white/20 text-white border-0 mb-3">
                        <Play className="h-3 w-3 mr-1" />
                        Grabación
                      </Badge>
                      <h3 className="text-white font-bold text-lg leading-tight">
                        {webinar.title}
                      </h3>
                      <p className="text-purple-200 text-sm mt-1">{webinar.instructor}</p>
                    </div>
                    
                    {/* Play overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                      <div className="h-14 w-14 rounded-full bg-white/20 flex items-center justify-center">
                        <Play className="h-6 w-6 text-white ml-1" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{webinar.duration_minutes} min</span>
                      <span>{formatDate(webinar.date)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">¿Por qué nuestros webinars?</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Tecnología de punta para una experiencia de aprendizaje sin límites
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6" />
              </div>
              <h3 className="font-semibold mb-2">Sin límite de asistentes</h3>
              <p className="text-sm text-gray-400">
                Usamos YouTube Live para transmisiones con miles de participantes simultáneos
              </p>
            </div>
            
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                <MessageSquare className="h-6 w-6" />
              </div>
              <h3 className="font-semibold mb-2">Chat interactivo</h3>
              <p className="text-sm text-gray-400">
                Participa en tiempo real con preguntas y comentarios durante la transmisión
              </p>
            </div>
            
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                <Play className="h-6 w-6" />
              </div>
              <h3 className="font-semibold mb-2">Grabaciones 24/7</h3>
              <p className="text-sm text-gray-400">
                Todos los webinars se graban y están disponibles en tu dashboard
              </p>
            </div>
            
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                <Bell className="h-6 w-6" />
              </div>
              <h3 className="font-semibold mb-2">Recordatorios</h3>
              <p className="text-sm text-gray-400">
                Recibe notificaciones por email antes de cada webinar registrado
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            ¿Listo para aprender con los expertos?
          </h2>
          <p className="text-gray-600 mb-8">
            Los webinars son gratuitos para todos los afiliados UPIM
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-gray-900 hover:bg-gray-800 h-12 px-8"
              asChild
            >
              <Link to="/afiliacion" data-testid="cta-affiliate">
                Afíliate ahora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="h-12 px-8"
              asChild
            >
              <Link to="/academia">
                Ver todos los cursos
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Unified Checkout Modal for Paid Webinars */}
      <UnifiedCheckout
        isOpen={checkoutModal.open}
        onClose={() => setCheckoutModal({ open: false, webinar: null })}
        product={checkoutModal.webinar ? {
          type: 'webinar',
          itemId: checkoutModal.webinar.id,
          name: checkoutModal.webinar.title,
          description: checkoutModal.webinar.description || 'Webinar en vivo',
          price: checkoutModal.webinar.price,
          features: [
            `${checkoutModal.webinar.duration_minutes || 60} minutos de contenido`,
            'Acceso a la transmisión en vivo',
            'Chat interactivo',
            'Grabación disponible 24/7'
          ]
        } : null}
        onSuccess={handleCheckoutComplete}
        successUrl={checkoutModal.webinar ? `${window.location.origin}/webinars/${checkoutModal.webinar.id}/pago-exitoso` : undefined}
        cancelUrl={`${window.location.origin}/webinars`}
      />
    </Layout>
  );
};

export default WebinarsPage;
